/**
 * Utility functions for image URL manipulation
 */

/**
 * Strips Cloudinary width and height transforms from image URLs
 * to get the original, full-resolution image.
 *
 * This function removes w_ (width) and h_ (height) transforms from Cloudinary URLs
 * while preserving other transforms and maintaining proper URL structure.
 *
 * Examples:
 * - "https://res.cloudinary.com/example/image/upload/w_400,h_300,c_fill/sample.jpg"
 *   → "https://res.cloudinary.com/example/image/upload/c_fill/sample.jpg"
 * - "https://res.cloudinary.com/example/image/upload/w_400/sample.jpg"
 *   → "https://res.cloudinary.com/example/image/upload/sample.jpg"
 *
 * @param url - The Cloudinary image URL to process
 * @returns The URL with width and height transforms removed
 */
export const removeCloudinarySizeTransforms = (url: string): string => {
  if (!url) return url;

  // Use a single regex with capture groups to match and reconstruct URL segments
  // that contain w_ or h_ transform patterns
  return url.replace(
    // Match URL segments that contain w_/h_ patterns with comma-separated transforms
    /\/([^/]*(?:[wh]_\d+[^/]*)+[^/]*)\//g,
    (match, transformSegment) => {
      // Split by comma, filter out w_/h_ transforms, rejoin
      const filteredTransforms = transformSegment
        .split(',')
        .filter((transform: string) => !transform.match(/^[wh]_\d+$/))
        .join(',');

      // Return the reconstructed segment, handling empty results
      return filteredTransforms ? `/${filteredTransforms}/` : '/';
    }
  );
};
